import api, { apiFormData } from './api';
import { ApiEndpoints } from './ApiUrls';
import { DocumentMetadata } from './documentService';

export interface CostTemplateItem {
  id: string;
  label: string;
  is_mandatory: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
  cost_template: string;
}

export interface CostTemplateDetail {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  company: string;
  created_by: string;
  updated_by: string;
  items: CostTemplateItem[];
}

export interface ActivityMovementSession {
  id: string;
  asset?: string | null;
  asset_detail?: { id: string; asset_tag: string; asset_name: string; name: string } | null;
  vehicle?: string | null;
  vehicle_detail?: { id: string; name: string; registration_number: string } | null;
  movement_type?: string | null;
  movement_type_display?: string | null;
  movement_status?: string | null;
  movement_status_display?: string | null;
  reader?: string | null;
  reader_detail?: {
    id: string;
    name: string;
    reader_type: string;
    reader_type_display: string;
    location_detail?: { id: string; name: string } | null;
  } | null;
  is_authorized: boolean;
  scanned_at?: string | null;
  force_closed_by?: string | null;
  force_closed_by_detail?: { id: string; name: string; email: string } | null;
  force_closure_comments?: string | null;
  created_at: string;
}

export interface ActivityDetail {
  id: string;
  taskId: string;
  task_id?: string; // For backward compatibility
  schedule?: {
    id: string;
    name: string;
    description: string;
  };
  schedule_detail?: any; // Full schedule object
  schedule_name?: string;
  is_one_off?: boolean;
  cost_template_detail?: CostTemplateDetail; // Full cost template object
  cost_template_name?: string;
  checklist_template_detail?: {
    id: string;
    name: string;
    items: Array<{
      id: string;
      question: string;
      display_order: number;
      checklist_template: string;
      label?: string;
      is_mandatory?: boolean;
      created_at?: string;
      updated_at?: string;
    }>;
    created_at?: string;
    updated_at?: string;
    company?: string;
    created_by?: string;
    updated_by?: string;
  };
  checklist_template_name?: string;
  checklist_items?: Array<{
    id: string;
    label: string;
    is_completed: boolean;
    checklist_template_item: string;
  }>;
  // Support both camelCase and snake_case versions
  activityType?: {
    id: string;
    name: string;
    payment_required: boolean;
    checklist_required: boolean;
    file_upload_required: boolean;
  };
  activity_type?: {
    id: string;
    name: string;
    payment_required: boolean;
    checklist_required: boolean;
    file_upload_required: boolean;
  };
  asset: {
    id: string;
    asset_name: string;
    location: {
      id: string;
      name: string;
    };
  } | null;
  // Support both camelCase and snake_case versions
  userGroup?: {
    id: string;
    name: string;
  } | null;
  user_group?: {
    id: string;
    name: string;
  } | null;
  // Support both camelCase and snake_case versions
  assignedUser?: {
    id: string;
    name: string;
    email: string;
  } | null;
  assigned_user_name?: string;
  // Support both camelCase and snake_case versions
  location?: {
    id: string;
    name: string;
  } | null;
  // Support both camelCase and snake_case versions
  dueDate?: string;
  due_date?: string;
  startDate?: string | null;
  start_date?: string | null;
  pauseDate?: string | null;
  pause_date?: string | null;
  resumeDate?: string | null;
  resume_date?: string | null;
  completionDate?: string | null;
  completion_date?: string | null;
  status: string;
  // Support both camelCase and snake_case versions
  timeTaken?: number;
  time_taken?: number;
  totalPausedDuration?: number;
  total_paused_duration?: number;
  active_duration?: number;
  slaStatus: 'OnTrack' | 'AtRisk' | 'Breached';
  costs: Array<{
    id: string;
    updated_by_name: string | null;
    label: string;
    value: number | null;
    created_at: string;
    updated_at: string;
    activity: string;
    cost_template_item: string;
    updated_by: string | null;
  }>;
  checklistItems: Array<{
    id: string;
    label: string;
    isCompleted?: boolean;
    is_completed?: boolean;
  }>;
  attachedFiles: Array<{
    fileId: string;
    fileName: string;
    fileSize: number;
    uploadedBy: {
      userId: string;
      name: string;
    };
    uploadedAt: string;
    downloadUrl: string;
  }>;
  statusHistory?: Array<{
    status: string;
    changedAt: string;
    changedBy: {
      userId: string;
      name: string;
    };
    comments: string;
  }>;
  documents_detail: DocumentMetadata[];
  movement_tracking_required?: boolean;
  tracking_type?: 'vehicle' | 'asset' | null;
  tracking_type_display?: string | null;
  vehicle_detail?: {
    id: string;
    name: string;
    registration_number: string;
    rfid_tag?: string;
  } | null;
}

export interface ActivityAccess {
  hasAccess: boolean;
  canStart: boolean;
  canPause: boolean;
  canResume: boolean;
  canComplete: boolean;
}

export interface StartActivityData {
  comments?: string;
}

export interface PauseActivityData {
  comments?: string;
}

export interface ResumeActivityData {
  comments?: string;
}

export interface CompletionCost {
  activityCostId: string;
  value: number;
}

export interface CompletionChecklistItem {
  activityChecklistId: string;
  isCompleted: boolean;
  is_completed?: boolean; // For backend compatibility
}

export interface UpdateChecklistItemsData {
  checklistItems: CompletionChecklistItem[];
}

export interface CompletionAttachment {
  fileName: string;
  fileSize: number;
  fileType: string;
  fileContent: string;
}

export interface CompleteActivityData {
  comments: string;
  costs?: CompletionCost[];
  checklist?: CompletionChecklistItem[];
  checklistItems?: CompletionChecklistItem[]; // Alternative property name for new flow
  attachments?: File[] | null;
  files?: File[]; // Alternative property name for new flow
  // Required to complete a movement-tracked activity whose check-out/check-in
  // scan is missing - the backend rejects the first attempt with
  // force_complete_required so the caller can re-submit with this set.
  force_complete?: boolean;
}

// Interface for one-off activity creation
export interface OneOffActivityCreateInput {
  name: string;
  asset_ids: string[];
  activity_type_id: string;
  user_group_id: string;
  start_date: string;
  execution_before: number;
  cost_template_id?: string;
  checklist_template_id?: string;
  description?: string;
  // Movement tracking fields
  movement_tracking_required?: boolean;
  tracking_type?: 'vehicle' | 'asset' | null;
  vehicle_id?: string | null;
}

const activityService = {
  // Get all activities
  getActivities: async (params: any): Promise<any> => {
    // Map backwards-compatible limit to page_size for the API
    const { limit, page_size, ...rest } = params || {};
    const requestParams = { ...rest, page_size: page_size ?? limit };
    const response = await api.get(
      ApiEndpoints.schedule.activities.list,
      { params: requestParams }
    );
    return response.data;
  },

  // Get pending activities
  getPendingActivities: async (params: any): Promise<any> => {
    const response = await api.get(
      ApiEndpoints.schedule.activities.pending,
      { params }
    );

    // Check if the response already has the expected structure
    if (response.data.activities && response.data.totalCount !== undefined) {
      return response.data;
    }

    // Fallback to the old structure if needed
    return {
      activities: response.data.results || response.data.activities || [],
      totalCount: response.data.count || response.data.totalCount || 0,
      page: response.data.page || 1,
      pageSize: response.data.pageSize || 10
    };
  },

  // Generate activity from schedule
  generateActivity: async (scheduleId: string): Promise<any> => {
    // Use the api instance that's already imported
    const response = await api.post(
      'schedule/schedules/generate_activities/',
      { schedule_id: scheduleId }
    );
    return response.data;
  },

  // Get activity details
  getActivityDetails: async (activityId: string): Promise<ActivityDetail> => {
    const response = await api.get(
      `${ApiEndpoints.schedule.activities.detail(activityId)}/details/`
    );
    return response.data;
  },

  // The RFID checkin/checkout audit trail for this activity (including
  // force-closed steps) - mirrors assetTransferService's transfer
  // movement-sessions call.
  getActivityMovementSessions: async (activityId: string): Promise<ActivityMovementSession[]> => {
    const response = await api.get(
      `${ApiEndpoints.schedule.activities.detail(activityId)}/movement-sessions/`
    );
    return response.data;
  },

  // Force-record this activity's check-out step without an RFID exit scan -
  // mirrors assetTransferService's force-checkout call. Callable any time
  // the activity is in progress, independently of completing it.
  forceCheckoutActivity: async (activityId: string, comments: string): Promise<ActivityDetail> => {
    const response = await api.post(
      `${ApiEndpoints.schedule.activities.detail(activityId)}/force-checkout/`,
      { comments }
    );
    return response.data;
  },

  // Force-record this activity's check-in step without an RFID entry scan -
  // mirrors assetTransferService's force-checkin call.
  forceCheckinActivity: async (activityId: string, comments: string): Promise<ActivityDetail> => {
    const response = await api.post(
      `${ApiEndpoints.schedule.activities.detail(activityId)}/force-checkin/`,
      { comments }
    );
    return response.data;
  },

  // Start activity — assigns activity to current user and transfers asset ownership
  startActivity: async (activityId: string): Promise<ActivityDetail> => {
    const response = await api.post(ApiEndpoints.schedule.activities.start(activityId));
    return response.data;
  },

  // Complete activity
  completeActivity: async (activityId: string, data: CompleteActivityData): Promise<ActivityDetail> => {
  
    // Handle both checklist and checklistItems properties
    const checklistData = data.checklist || data.checklistItems || [];
    const checklist = checklistData.map(item => ({
      ...item,
      is_completed: item.isCompleted,
    }));

    const formData = new FormData();

    if (data.comments) {
      formData.append('comments', data.comments);
    }

    if (data.costs?.length) {
      formData.append('costs', JSON.stringify(data.costs));
    }

    if (checklist.length) {
      formData.append('checklist', JSON.stringify(checklist));
    }

    if (data.force_complete) {
      formData.append('force_complete', 'true');
    }

    // Handle both attachments and files properties
    const filesToUpload = data.attachments || data.files || [];
    if (filesToUpload.length) {
      filesToUpload.forEach((attachment, index) => {
        formData.append('documents', attachment);
      });
    }

    const response = await apiFormData.post(
      `${ApiEndpoints.schedule.activities.detail(activityId)}/complete/`,
      formData
    );

    return response.data;
  },

  // Update checklist items
  updateChecklistItems: async (activityId: string, data: UpdateChecklistItemsData): Promise<ActivityDetail> => {
    // Ensure checklist items have both isCompleted and is_completed properties
    if (data.checklistItems && data.checklistItems.length > 0) {
      data.checklistItems = data.checklistItems.map(item => ({
        ...item,
        is_completed: item.isCompleted // Ensure is_completed is set
      }));
    }

    console.log('Updating checklist items:', data);

    const response = await api.post(
      ApiEndpoints.schedule.activities.updateChecklist(activityId),
      data
    );

    console.log('Update checklist response:', response.data);
    return response.data;
  },

  // Get file download URL
  getFileDownloadUrl: (fileId: string): string => {
    return `/api/files/${fileId}/download`;
  },

  // Create one-off activity
  createOneOffActivity: async (data: OneOffActivityCreateInput): Promise<any> => {
    console.log('Creating one-off activity with data:', data);

    // Set default values for movement tracking if not provided
    const requestData = {
      ...data,
      movement_tracking_required: data.movement_tracking_required || false,
      tracking_type: data.movement_tracking_required ? data.tracking_type : null,
      vehicle_id: data.movement_tracking_required && data.tracking_type === 'vehicle' ? data.vehicle_id : null
    };

    const response = await api.post(
      ApiEndpoints.schedule.activities.createOneOff,
      requestData
    );

    console.log('One-off activity creation response:', response.data);
    return response.data;
  }
};

export default activityService;